import { Injectable, NgZone } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Observable } from 'rxjs';

import { CreateEventFeaturesService } from '../../core/services/create-event-features.service';
import { CaptureEnd, CaptureEvent, CaptureFailure, CaptureOptions, CaptureSession } from './capture-types';

/**
 * How "Say it instead" hears the host: records a short clip with
 * MediaRecorder and sends it to our speech-to-text endpoint
 * (pwa-transcribe-voice Lambda → Groq Whisper by default). The browser's own
 * speech recognition is deliberately not used: one engine behaves the same
 * on every browser, and the service behind it is ours to change.
 *
 * There is no live transcript: the words arrive once the clip has been sent.
 * Silence detection is ours (a Web Audio level meter),
 * and a clip that never got loud enough is not sent at all — Whisper invents
 * words ("Thank you.") when given silence.
 */

/** Level (RMS) that counts as speech, and how many 100 ms frames of it. */
const SPEECH_LEVEL = 0.02;
const SPEECH_FRAMES = 3;
/** Below this, once speech has been heard, is silence. */
const SILENCE_LEVEL = 0.012;

const PREFERRED_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];

@Injectable({ providedIn: 'root' })
export class AudioCaptureService {
  constructor(
    private zone: NgZone,
    private afAuth: AngularFireAuth,
    private features: CreateEventFeaturesService,
  ) {}

  /** A recorder, a mic API and somewhere to send the clip. */
  isSupported(): boolean {
    return !!this.features.voiceTranscribeUrl
      && typeof (window as any).MediaRecorder !== 'undefined'
      && !!navigator.mediaDevices?.getUserMedia;
  }

  start(options: CaptureOptions): CaptureSession {
    let stopReason: CaptureEnd = 'done';
    let recorder: MediaRecorder | null = null;
    let aborted = false;
    let finish: ((reason: CaptureEnd) => void) | null = null;

    const events$ = new Observable<CaptureEvent>(observer => {
      const emit = (e: CaptureEvent) => this.zone.run(() => observer.next(e));
      const done = () => this.zone.run(() => observer.complete());

      let stream: MediaStream | null = null;
      let audioCtx: AudioContext | null = null;
      let meterTimer: any = null;
      let limitTimer: any = null;
      const chunks: Blob[] = [];
      let startedAt = Date.now();
      let meterWorks = false;
      let loudFrames = 0;
      let heard = false;
      let quietSince: number | null = null;

      const cleanup = () => {
        clearInterval(meterTimer);
        clearTimeout(limitTimer);
        stream?.getTracks().forEach(t => t.stop());
        audioCtx?.close().catch(() => undefined);
      };

      finish = (reason: CaptureEnd) => {
        stopReason = reason;
        if (recorder && recorder.state !== 'inactive') recorder.stop();
      };

      navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 } })
        .then(s => {
          if (aborted) {
            s.getTracks().forEach(t => t.stop());
            return;
          }
          stream = s;
          const type = PREFERRED_TYPES.find(t => (window as any).MediaRecorder.isTypeSupported?.(t));
          recorder = type ? new MediaRecorder(s, { mimeType: type }) : new MediaRecorder(s);
          recorder.ondataavailable = e => { if (e.data?.size) chunks.push(e.data); };
          recorder.onstop = () => {
            cleanup();
            if (aborted) return done();
            const seconds = Math.round((Date.now() - startedAt) / 1000);
            const mimeType = recorder?.mimeType || type || 'audio/webm';
            const clip = new Blob(chunks, { type: mimeType });
            // Nothing loud enough was ever heard: don't send silence to Whisper.
            if ((meterWorks && !heard) || clip.size < 1000) {
              emit({ type: 'error', reason: 'no_speech' });
              return done();
            }
            emit({ type: 'transcribing' });
            this.transcribe(clip, mimeType, options.lang)
              .then(text => {
                if (aborted) return;
                if (text) emit({ type: 'final', text, endedBy: stopReason, seconds });
                else emit({ type: 'error', reason: 'no_speech' });
              })
              .catch((reason: CaptureFailure) => { if (!aborted) emit({ type: 'error', reason }); })
              .finally(done);
          };

          recorder.start(250);
          startedAt = Date.now();
          emit({ type: 'started' });
          limitTimer = setTimeout(() => finish?.('limit'), options.maxSeconds * 1000);

          // Silence detection. Optional: without it the host just taps Done.
          try {
            const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
            audioCtx = new Ctx() as AudioContext;
            const analyser = audioCtx.createAnalyser();
            analyser.fftSize = 2048;
            audioCtx.createMediaStreamSource(s).connect(analyser);
            const buf = new Float32Array(analyser.fftSize);
            meterWorks = true;
            meterTimer = setInterval(() => {
              analyser.getFloatTimeDomainData(buf);
              let sum = 0;
              for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
              const level = Math.sqrt(sum / buf.length);
              if (level > SPEECH_LEVEL) {
                loudFrames++;
                if (loudFrames >= SPEECH_FRAMES) heard = true;
                quietSince = null;
              } else if (heard && level < SILENCE_LEVEL) {
                quietSince = quietSince ?? Date.now();
                if (Date.now() - quietSince >= options.silenceMs) finish?.('silence');
              }
            }, 100);
          } catch {
            meterWorks = false;
          }
        })
        .catch(err => {
          const name = err?.name || '';
          const reason: CaptureFailure =
            name === 'NotAllowedError' || name === 'SecurityError' || name === 'NotFoundError' ? 'mic_blocked' : 'unsupported';
          if (!aborted) emit({ type: 'error', reason });
          done();
        });

      return () => {
        aborted = true;
        if (recorder && recorder.state !== 'inactive') recorder.stop();
        cleanup();
      };
    });

    return {
      events$,
      stop: () => finish?.('done'),
      abort: () => {
        aborted = true;
        if (recorder && recorder.state !== 'inactive') recorder.stop();
      },
    };
  }

  /** Sends the clip; resolves with the text ('' = nothing said), rejects with a CaptureFailure. */
  private async transcribe(clip: Blob, mimeType: string, lang: string): Promise<string> {
    if (isOffline()) throw 'offline' as CaptureFailure;

    const audio = await blobToBase64(clip);
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    // Optional: before sign-in (/auth/start) there is no token, and that's fine.
    try {
      const user = await this.afAuth.currentUser;
      const token = user ? await user.getIdToken() : null;
      if (token) headers['Authorization'] = token;
    } catch { /* anonymous is allowed */ }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const res = await fetch(this.features.voiceTranscribeUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ audio, mimeType, lang }),
        signal: controller.signal,
      });
      if (res.status === 429) throw 'rate_limited' as CaptureFailure;
      if (!res.ok) throw 'transcribe_failed' as CaptureFailure;
      const json = await res.json().catch(() => ({}));
      return typeof json?.text === 'string' ? json.text.trim() : '';
    } catch (err) {
      if (err === 'rate_limited' || err === 'transcribe_failed') throw err;
      throw (isOffline() ? 'offline' : 'transcribe_failed') as CaptureFailure;
    } finally {
      clearTimeout(timer);
    }
  }
}

/** A function, not an inline check: the answer can change across the awaits above. */
function isOffline(): boolean {
  return navigator.onLine === false;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
